import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type {
  OrganizationEditDialogForm,
  OrganizationNewInSFDTO
} from './OrganizationEditDialog.types';

export const OrganizationEditDialogContentNew = ({
  organizations
}: {
  organizations: OrganizationNewInSFDTO[];
}) => {
  const [orgType, setOrgType] = useState<'default' | 'ir'>('default');
  const {
    control,
    setValue,
    formState: { errors },
    register
  } = useFormContext<OrganizationEditDialogForm>();

  const orgLabel = useWatch({ control, name: 'label' });

  const handleOrganizationChange = (event: SelectChangeEvent<number>): void => {
    const {
      name,
      short_code: label,
      uci
    } = organizations.find((org) => org.id === +event.target.value)!;
    setValue('id', +event.target.value);
    setValue('name', name);
    setValue('label', label || '');
    setValue('uci', uci || '');
  };

  const handleOrgTypeChange = (type: 'default' | 'ir'): void => {
    setOrgType(type);
    setValue('is_ir', type === 'ir');

    if (type === 'ir') {
      setValue('name', '');
      setValue('label', '');
      setValue('uci', '');
    }
  };

  if (organizations.length === 0) {
    return (
      <Alert variant="outlined" color="info">
        There are currently no new organization from Salesforce to link to
        Scope.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mt: 2 }}>
        <ToggleButtonGroup size="small" exclusive>
          <ToggleButton
            value="default"
            selected={orgType === 'default'}
            onChange={() => handleOrgTypeChange('default')}
            sx={{ px: 2 }}
          >
            Default Organization
          </ToggleButton>
          <ToggleButton
            value="ir"
            selected={orgType === 'ir'}
            onChange={() => handleOrgTypeChange('ir')}
            sx={{ px: 2 }}
          >
            IR Organization
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {orgType === 'default' && (
        <>
          <Alert variant="outlined" color="info" sx={{ my: 2 }}>
            All production organizations should be created and managed in
            Salesforce. This list is synced with Salesforce twice a day.
          </Alert>
          <InputLabel id="selectOrganization">Organization</InputLabel>
          <FormHelperText>
            Select a new organization from Salesforce to link it to Scope
          </FormHelperText>

          <FormControl fullWidth>
            <Controller
              control={control}
              name="id"
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Select<number>
                  {...field}
                  onChange={handleOrganizationChange}
                  value={field.value || ''}
                  MenuProps={{
                    style: {
                      maxHeight: '30vh'
                    }
                  }}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      <Stack
                        direction="row"
                        sx={{ justifyContent: 'space-between', width: '100%' }}
                        spacing={1}
                      >
                        <Box>{org.name}</Box>
                        <Box sx={{ color: 'text.secondary' }}>
                          {org.short_code}
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </>
      )}
      {orgType === 'ir' && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <InputLabel htmlFor="label">Organization Name</InputLabel>
            <TextField
              {...register('name', {
                required: 'Organization name is required'
              })}
              fullWidth
              required
              variant="outlined"
              error={!!errors.name}
              helperText={errors.name?.message}
              margin="dense"
            />
          </Box>
          <Box>
            <InputLabel htmlFor="label">Organization Shortcode</InputLabel>
            <TextField
              {...register('label', {
                required: 'Organization shortcode is required',
                minLength: {
                  value: 3,
                  message: 'Shortcode must be at least 3 characters long'
                },
                maxLength: {
                  value: 10,
                  message: 'Shortcode must be max 10 characters long'
                },
                pattern: {
                  value: /^[a-zA-Z0-9_]+$/,
                  message:
                    'Shortcode can only contain letters, numbers, and underscores'
                }
              })}
              id="ir-org-shortcode"
              required
              fullWidth
              variant="outlined"
              error={!!errors.label}
              helperText={errors.label?.message}
              margin="dense"
            />
            {orgLabel && !errors.label?.message && (
              <FormHelperText>
                Preview final shortcode: <strong>ir_{orgLabel}</strong>
              </FormHelperText>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default OrganizationEditDialogContentNew;
